import { useMemo } from 'react';

import PropTypes from 'prop-types';

import { useIntl } from '@zenbusiness/application-commons-intl';
import Card from '@zenbusiness/zui/Card';
import Heading from '@zenbusiness/zui/Heading';
import List from '@zenbusiness/zui/List';
import Stack from '@zenbusiness/zui/Stack';

import ListItem from '../ListItem';

const MAX_ITEMS_COUNT = 10;

const buildMessageIds = (messageKey) => ({
  title: `${messageKey}.heading`,
  body: `${messageKey}.body`,
  item: (itemKey) => ({
    title: `${messageKey}.${itemKey}.heading`,
    body: `${messageKey}.${itemKey}.body`
  })
});

function Section({ messageKey, messageValues, ...delegated }) {
  const { messages } = useIntl();

  const messageIds = useMemo(() => buildMessageIds(messageKey), [messageKey]);

  const title = messages[messageIds.title];
  const body = messages[messageIds.body];
  const items = useMemo(() => {
    const result = [];

    for (let idx = 0; idx < MAX_ITEMS_COUNT; idx += 1) {
      const itemKey = idx + 1;
      const itemIds = messageIds.item(itemKey);

      if (!messages[itemIds.title] && !messages[itemIds.body]) {
        break;
      }

      result.push({
        id: itemKey,
        messageIds: itemIds
      });
    }

    return result;
  }, [messageIds, messages]);

  return (
    <Stack component="section" spacing={2} {...delegated}>
      {title && <Heading size="sm">{title}</Heading>}

      <Card sx={(theme) => ({ borderColor: theme.palette.border.main })}>
        <List sx={(theme) => ({ margin: `0 ${theme.spacing(2)}` })}>
          {body && (
            <ListItem messageIds={{ body: messageIds.body }} messageValues={messageValues} />
          )}

          {items.map((item) => (
            <ListItem
              key={item.id}
              divider
              messageIds={item.messageIds}
              messageValues={messageValues}
            />
          ))}
        </List>
      </Card>
    </Stack>
  );
}

Section.propTypes = {
  messageKey: PropTypes.string.isRequired,
  messageValues: PropTypes.object
};

export default Section;
